import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import HoverTooltip from "../HoverTooltip.jsx";
import OverflowTooltip from "../OverflowTooltip.jsx";
import { LITE_UNSETTLED_LABEL, LITE_VERDICT_LABEL, liteSessionMix, liteUnsettledKind, ribbonBins } from "../../lib/triageLite.js";
import { fetchSessionTurns } from "../../lib/triageTimeline.js";
import { summaryInput, summaryKey } from "../../lib/findingSummary.js";
import { getSuggestContext, subscribeSuggestContext } from "../../lib/standardSuggest.js";
import { openStandardTuning, standardFromLabel } from "../../lib/standardTune.js";
import { getTriageChatHost, subscribeTriageChatHost } from "../../lib/triageChatStore.js";
import TriageSessionPreview from "./TriageSessionPreview.jsx";
import FindingSummary from "./FindingSummary.jsx";
import { ACTION_CHIP, CAPS_CHIP, CHECK_CHIP, Disclosure, FILE_LABEL, RUNG_CHIP, SeverityWord } from "./triageParts.jsx";

// What the check means, in the reader's terms rather than the contract's.
export const VERDICT_LABEL = LITE_VERDICT_LABEL;
export const VERDICT_NOTE = {
  confirmed: "the session shows it happened as described",
  partial: "it happened, but not quite as the finding says",
  refuted: "the session shows it didn't happen",
  unverifiable: "not enough in the session to say either way",
  non_adverse: "the finding describes something, not something the agent did",
};
// "Couldn't check", said as what actually happened.
export const UNSETTLED_LABEL = LITE_UNSETTLED_LABEL;
const UNSETTLED_NOTE = {
  not_loaded: "the quoted message was not among what the check could load, so nothing was judged; worth running again once the record read is updated",
  outcome_unseen: "the session shows the act, but not what came of it",
  unsettled: "the check read the citation but could not settle what the agent did",
  unfinished: "the verifier ended before it settled this finding",
};
export const labelOf = (v) => (v?.verdict === "unverifiable" ? UNSETTLED_LABEL[liteUnsettledKind(v)] : null) || VERDICT_LABEL[v?.verdict] || v?.verdict || "";
const refutedOnCitation = (v) => v?.verdict === "refuted" && (v.claims || []).some((c) => c.kind === "conduct" && c.basis === "citation");
const noteOf = (v) => (v?.verdict === "unverifiable" ? UNSETTLED_NOTE[liteUnsettledKind(v)] : null)
  || (refutedOnCitation(v) ? "what the finding cites is not this agent doing it" : null)
  || VERDICT_NOTE[v?.verdict] || "";
// Why a finding was never put to the standard. Only one that survived the
// evidence is a candidate; the rest say so rather than sitting silent, because
// "not escalated" and "never asked" are different answers.
const NOT_ASKED = {
  refuted: "not asked — the finding is false",
  unverifiable: "not asked — the finding couldn't be checked",
  non_adverse: "not asked — there is no action to judge",
};
// The part of the finding a partial got wrong, in the reader's words.
const FAILED_CLAIM_COPY = {
  conduct: "it overstates what the agent did",
  effect: "it overstates what came of it",
  scope: "it overstates what was affected",
  authorisation: "it was actually allowed",
  framing: "it's worded too strongly",
};
const MIX_ORDER = ["confirmed", "partial", "refuted", "non_adverse", "unverifiable"];
const MIX_FILL = {
  confirmed: "var(--ink)",
  partial: "var(--muted)",
  refuted: "var(--line-strong)",
  non_adverse: "var(--line-strong)",
  unverifiable: "repeating-linear-gradient(135deg, var(--line-strong) 0 3px, var(--line-soft) 3px 6px)",
};
// The check's answer in the Triage tab's check chips: filled where the
// session bears the finding out, outlined where it partly does or does not,
// dashed where nothing could be settled.
const VERDICT_CHIP = {
  confirmed: CHECK_CHIP.holds[1],
  partial: CHECK_CHIP.overstated[1],
  refuted: CHECK_CHIP.misread[1],
  unverifiable: CHECK_CHIP.unchecked[1],
  non_adverse: RUNG_CHIP.noise,
};
// Past a member's disclosure, number, severity and the gaps between: where
// its own reading starts, under its title.
const MEMBER_BODY = 14 + 8 + 22 + 8 + 34 + 8;

export const membersOf = (job) => (job?.findings?.length ? job.findings : [job]);

// The chip names the answer; what an unsettled one actually came to, and
// what the answer means, are on hover.
export function VerdictChip({ verdict }) {
  return (
    <HoverTooltip label={`${labelOf(verdict)}: ${noteOf(verdict)}`} placement="top" wide>
      <span data-lite-verdict={verdict.verdict} className={`${CAPS_CHIP} ${VERDICT_CHIP[verdict.verdict] || VERDICT_CHIP.unverifiable}`}>
        {VERDICT_LABEL[verdict.verdict] || verdict.verdict}
      </span>
    </HoverTooltip>
  );
}

const range = (turns) => {
  if (!turns?.length) return "not located";
  const lo = Math.min(...turns), hi = Math.max(...turns);
  return lo === hi ? `T${lo}` : `T${lo}–T${hi}`;
};

// A session's verdicts at a glance: the row carries every finding's answer,
// not only the one its title came from.
export function LiteVerdictMix({ job, verdicts }) {
  const members = membersOf(job);
  const mix = liteSessionMix(members, verdicts);
  if (!mix.settled) return null;
  const kinds = MIX_ORDER.filter((k) => mix.counts[k]);
  const unsettled = {};
  for (const m of members) {
    const kind = liteUnsettledKind(verdicts?.[m.id]);
    if (kind) unsettled[kind] = (unsettled[kind] || 0) + 1;
  }
  const words = (k) => (k === "unverifiable"
    ? Object.entries(unsettled).map(([u, n]) => `${n} ${UNSETTLED_LABEL[u].toLowerCase()}`).join(" · ")
    : `${mix.counts[k]} ${VERDICT_LABEL[k].toLowerCase()}`);
  const label = `${mix.escalated ? `${mix.escalated} escalated \u00b7 ` : ""}${kinds.map(words).join(" \u00b7 ")}`;
  return (
    <span data-lite-mix="" className="grid w-[150px] shrink-0 gap-[3px]">
      <span className="flex h-[4px] gap-[1px]" aria-hidden="true">
        {kinds.map((k) => (
          <span key={k} className="rounded-[1px]" style={{ flexGrow: mix.counts[k], flexBasis: 0, background: MIX_FILL[k] }} />
        ))}
      </span>
      <OverflowTooltip text={label} className="min-w-0 truncate text-[9px] leading-[1.2] text-[color:var(--muted)]" />
    </span>
  );
}

// One finding's answer, laid out as the Triage tab's case file: what the
// check found and the standard's answer, beside the quotes it rests on.
export function LiteVerdictCard({ member, verdict, conduct, onOpen }) {
  const failed = verdict.failedClaim && verdict.failedClaim !== "none" ? FAILED_CLAIM_COPY[verdict.failedClaim] || verdict.failedClaim : "";
  return (
    <div data-lite-card="" className="@container cursor-auto pt-[4px] pb-[10px]" style={{ paddingLeft: MEMBER_BODY }}>
      <div className="grid grid-cols-2 gap-x-[32px] gap-y-[14px] @max-[720px]:grid-cols-1">
        <div className="grid min-w-0 content-start gap-[12px]">
          <section className="grid gap-[4px]">
            <span className={FILE_LABEL}>Check</span>
            <span className="text-[11px] leading-[1.4] text-[color:var(--muted)]">
              {noteOf(verdict)}{failed ? ` \u00b7 ${failed}` : ""}{conduct ? ` \u00b7 ${conduct}` : ""}
            </span>
            {verdict.why ? <p className="m-0 text-[11px] leading-[1.5] text-[color:var(--ink)] [overflow-wrap:anywhere]">{verdict.why}</p> : null}
          </section>
          {verdict.correctedClaim ? (
            <section className="grid gap-[4px]">
              <span className={FILE_LABEL}>Session shows</span>
              <p className="m-0 text-[11px] leading-[1.5] text-[color:var(--ink)] [overflow-wrap:anywhere]">{verdict.correctedClaim}</p>
            </section>
          ) : null}
          {/* The standard's own answer: met, not met, or never put. Without it a
              reader cannot tell a considered no from an unasked one. */}
          <section data-lite-standard-verdict="" className="grid gap-[4px]">
            <span className={FILE_LABEL}>Standard</span>
            <span className="text-[11px] leading-[1.4]" style={{ color: verdict.escalates ? "var(--ink)" : "var(--muted)" }}>
              {verdict.escalates ? "met — escalated" : verdict.standardAsked ? "not met" : NOT_ASKED[verdict.verdict] || "not asked"}
            </span>
            {/* A standard never put has nothing of its own to say; the line above
                already gives the reason, and the ledger's filler only repeats it. */}
            {verdict.standardWhy && (verdict.standardAsked || verdict.escalates) ? (
              <p className={`m-0 text-[11px] leading-[1.5] [overflow-wrap:anywhere] ${verdict.escalates ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]"}`}>{verdict.standardWhy}</p>
            ) : null}
            {verdict.withheld ? <span className="text-[11px] leading-[1.4] text-[color:var(--muted)]">It meets the standard as worded, but the check left nothing true enough to escalate.</span> : null}
            {verdict.unconfirmed ? <span className="text-[11px] leading-[1.4] text-[color:var(--muted)]">Asked twice and the answers disagreed, so it is not escalated.</span> : null}
          </section>
          <span>
            <button type="button" data-lite-open-session="" onClick={(e) => { e.stopPropagation(); onOpen(member); }} className={ACTION_CHIP}>
              Open the session
            </button>
          </span>
        </div>
        <section className="grid min-w-0 content-start gap-[4px]">
          <span className={FILE_LABEL}>{verdict.evidence?.length ? "Quoted from the session" : "Evidence"}</span>
          {verdict.evidence?.length ? (
            <ul data-lite-evidence="" className="m-0 grid list-none p-0">
              {verdict.evidence.map((e) => (
                <li key={e.id} className="grid gap-x-[10px] border-t border-[color:var(--line-soft)] py-[7px] first:border-t-0 first:pt-[3px] [grid-template-columns:40px_minmax(0,1fr)]">
                  <span className="grid content-start justify-items-start gap-[2px] font-mono text-[10px] leading-[1.5] text-[color:var(--muted-soft)]">
                    <span className="font-sans text-[9px]">{e.id}</span>
                    {e.turn != null ? <span>T{e.turn}</span> : null}
                  </span>
                  <span className="font-mono text-[10px] leading-[1.5] whitespace-pre-wrap text-[color:var(--ink)] [overflow-wrap:anywhere]">{e.text}</span>
                </li>
              ))}
            </ul>
          ) : (
            <span className="text-[11px] leading-[1.4] text-[color:var(--muted)]">
              Nothing it quoted could be matched to what it read, so the finding has no place on the ribbon.
            </span>
          )}
        </section>
      </div>
    </div>
  );
}

// What the check found of one finding, behind "How the check read it".
// `standard`: the standard's own sentence leads, where a summary stood in for it.
function CheckReading({ verdict, conduct = null, standard = false }) {
  const failed = verdict.failedClaim && verdict.failedClaim !== "none" ? FAILED_CLAIM_COPY[verdict.failedClaim] || verdict.failedClaim : "";
  return (
    <>
      {standard && verdict.standardWhy ? (
        <p className="m-0 text-[color:var(--ink)] [overflow-wrap:anywhere]" data-lite-brief-standard="">
          {verdict.metBy?.length ? <span className="text-[color:var(--muted)]">Met by {verdict.metBy.join(", ")}: </span> : null}
          {verdict.standardWhy}
        </p>
      ) : null}
      <span className="text-[color:var(--muted)]">{noteOf(verdict)}{failed ? ` · ${failed}` : ""}{conduct ? ` · ${conduct}` : ""}</span>
      {verdict.why ? <p className="m-0 text-[color:var(--ink)] [overflow-wrap:anywhere]">{verdict.why}</p> : null}
      {verdict.correctedClaim ? <p className="m-0 text-[color:var(--ink)] [overflow-wrap:anywhere]"><span className="text-[color:var(--muted)]">The session shows: </span>{verdict.correctedClaim}</p> : null}
    </>
  );
}

// One finding's answer in brief, for a host that shows its evidence itself:
// why the standard flagged it (or did not), what the check found on request.
export function LiteVerdictBrief({ member, verdict, conduct = null }) {
  const [open, setOpen] = useState(false);
  const said = verdict.standardWhy && (verdict.standardAsked || verdict.escalates);
  const toggle = () => setOpen((v) => !v);
  return (
    <div data-lite-brief="" className="grid cursor-auto gap-[4px]">
      {said ? (
        <p className={`m-0 text-[11px] leading-[1.5] [overflow-wrap:anywhere] ${verdict.escalates ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]"}`}>
          {verdict.escalates ? null : <span className="text-[color:var(--ink)]">Not flagged by the standard: </span>}
          {verdict.standardWhy}
          {verdict.escalates ? <WrongFlag findings={[{ id: member.id, title: member.title, verdict }]} /> : null}
        </p>
      ) : null}
      {!said && !verdict.escalates ? <span className="text-[11px] leading-[1.4] text-[color:var(--muted)]">{NOT_ASKED[verdict.verdict] || "Not put to a standard"}</span> : null}
      {verdict.withheld ? <span className="text-[11px] leading-[1.4] text-[color:var(--muted)]">It meets the standard as worded, but the check left nothing true enough to escalate.</span> : null}
      {verdict.unconfirmed ? <span className="text-[11px] leading-[1.4] text-[color:var(--muted)]">Asked twice and the answers disagreed, so it is not escalated.</span> : null}
      <span className="flex items-center gap-x-[2px]">
        <Disclosure open={open} label={open ? "Close how the check read it" : "Show how the check read it"} onToggle={toggle} />
        <button type="button" onClick={toggle} className="cursor-pointer bg-transparent p-0 text-[11px] leading-[1.4] text-[color:var(--muted)] hover:text-[color:var(--ink)]" data-lite-brief-read="">How the check read it</button>
      </span>
      {open ? (
        <div data-lite-brief-check="" className="grid gap-[4px] pl-[18px] text-[11px] leading-[1.5]">
          <CheckReading verdict={verdict} conduct={conduct} />
        </div>
      ) : null}
    </div>
  );
}

// Under the summary of the findings that met a standard: one way to say the
// flags were wrong and one "How the check read it", for them all. With
// several, the reader picks which were wrong. `met`: [{ member, verdict,
// conduct }].
export function SummaryTools({ met }) {
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen((v) => !v);
  const several = met.length > 1;
  return (
    <div data-lite-summary-tools="" className="grid cursor-auto gap-[4px]">
      <span data-lite-brief-tools="" className="flex flex-wrap items-baseline gap-x-[8px] text-[11px] leading-[1.4]">
        <WrongFlag findings={met.map(({ member, verdict }) => ({ id: member.id, title: member.title, verdict }))} />
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-label={open ? "Hide how the check read it" : "How the check read it"}
          title={open ? "Hide how the check read it" : "How the check read it"}
          className={`inline-flex cursor-pointer items-center self-center bg-transparent p-0 hover:text-[color:var(--ink)] ${open ? "text-[color:var(--ink)]" : "text-[color:var(--muted-soft)]"}`}
          data-lite-brief-read=""
        >
          <svg viewBox="0 0 12 12" className="h-[12px] w-[12px]" fill="none" stroke="currentColor" strokeWidth="1.1" aria-hidden="true">
            <circle cx="6" cy="6" r="5" />
            <path d="M6 5.5v3" strokeLinecap="round" />
            <circle cx="6" cy="3.4" r="0.6" fill="currentColor" stroke="none" />
          </svg>
        </button>
      </span>
      {open ? (
        <div data-lite-brief-check="" className="grid max-w-[560px] gap-[10px] rounded-[4px] border border-[color:var(--line-strong)] bg-[color:var(--panel)] px-[10px] py-[8px] text-[11px] leading-[1.5]">
          {met.map(({ member, verdict, conduct }) => (
            <div key={member.id} className="grid gap-[4px]" data-lite-brief-reading={member.id}>
              {several ? <span className="font-medium text-[color:var(--ink)]">{member.title}</span> : null}
              <CheckReading verdict={verdict} conduct={conduct} standard />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// The opened session: its workspace card in miniature, as the trace page shows
// it, and the flagged findings under it with the turns each was cited at; the
// finding chosen opens in place.

// A flag the reader thinks is wrong opens a chat that tunes the standard that
// made it, with the findings it should not have flagged. Only where a chat can
// open and the tab can check a rewording.
// The standard is the first that met it, or, with one standard kept, the one
// the verdict was answered by.
function useTuneStandard() {
  const canAsk = useSyncExternalStore(subscribeTriageChatHost, () => typeof getTriageChatHost()?.ask === "function", () => false);
  const ctx = useSyncExternalStore(subscribeSuggestContext, getSuggestContext, () => null);
  return (verdict) => (canAsk && ctx?.sensitive
    ? standardFromLabel(ctx, verdict?.metBy?.[0]) || (ctx.standards || []).find((s) => s.digest && s.digest === verdict?.standardDigest) || null
    : null);
}
function tune(item, ids) {
  try { openStandardTuning({ host: getTriageChatHost(), standardId: item.standardId, wrongIds: ids }); } catch { /* shown only where a chat can open */ }
}
const LINK = "cursor-pointer border-0 bg-transparent p-0 text-[11px] leading-[1.4] text-[color:var(--muted)] underline decoration-dotted underline-offset-2 hover:text-[color:var(--ink)]";

// `findings`: [{ id, title, verdict }]. One is tuned at once; with several, a
// click asks which were wrong, then each standard they came from is tuned
// with its own.
function WrongFlag({ findings }) {
  const standardOf = useTuneStandard();
  const tunable = findings.map((f) => ({ ...f, item: standardOf(f.verdict) })).filter((f) => f.item);
  const [picking, setPicking] = useState(false);
  // Every one starts picked: the reader unpicks the flags that were right.
  const [kept, setKept] = useState(() => new Set());
  if (!tunable.length) return null;
  const one = tunable.length === 1 ? tunable[0] : null;
  const click = (e) => {
    e.stopPropagation();
    if (one) tune(one.item, [one.id]);
    else setPicking((v) => !v);
  };
  const flip = (id) => setKept((cur) => {
    const next = new Set(cur);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  // The picked findings by the standard that flagged them, a chat each.
  const groups = [...tunable.filter((f) => !kept.has(f.id)).reduce((by, f) => {
    const g = by.get(f.item.standardId) || { item: f.item, ids: [] };
    g.ids.push(f.id);
    return by.set(f.item.standardId, g);
  }, new Map()).values()];
  // Part of the sentence that says why it met, so disagreeing reads as the
  // natural next thought rather than one more button. The picker wraps under
  // whatever sits beside it.
  return (
    <>
      {" "}
      <button
        type="button"
        data-lite-wrong-flag={one ? one.id : ""}
        onClick={click}
        aria-expanded={one ? undefined : picking}
        title={one ? `Opens a chat that tunes ${one.item.name} with this finding as one it shouldn't flag` : "Choose which findings it shouldn't have flagged, then tune the standard"}
        className={LINK}
      >
        Not something to flag? Tune it
      </button>
      {picking && !one ? (
        <span data-lite-wrong-pick="" className="order-last grid basis-full gap-[4px] pt-[4px]" onClick={(e) => e.stopPropagation()}>
          <span className="text-[color:var(--muted)]">Which shouldn&rsquo;t have been flagged?</span>
          {tunable.map((f) => (
            <label key={f.id} className="flex cursor-pointer items-baseline gap-x-[6px] text-[color:var(--ink)]">
              <input type="checkbox" checked={!kept.has(f.id)} onChange={() => flip(f.id)} data-lite-wrong-pick-finding={f.id} />
              <span className="min-w-0 [overflow-wrap:anywhere]">{f.title}</span>
              <span className="shrink-0 text-[color:var(--muted)]">{f.item.name}</span>
            </label>
          ))}
          <span className="flex flex-wrap gap-x-[8px] gap-y-[4px] pt-[2px]">
            {groups.length ? groups.map(({ item, ids }) => (
              <button key={item.standardId} type="button" data-lite-wrong-tune={item.standardId} onClick={() => { tune(item, ids); setPicking(false); }} className={ACTION_CHIP}>
                Tune {item.name}
              </button>
            )) : <span className="text-[color:var(--muted)]">Pick at least one.</span>}
          </span>
        </span>
      ) : null}
    </>
  );
}

// `inset` and `insetEnd`: how far in from the left and from the right it
// sits, as the row it opens under has it.
export function LiteSessionPanel({ job, verdicts, conductName, onOpen, inset = 0, insetEnd = 0, summary = null }) {
  const members = useMemo(() => membersOf(job), [job]);
  // The turns of the messages the verifier quoted and the review cited, read
  // from the session's record. A quote is placed at its message; a finding
  // with no placed quote falls back to where its review cited.
  const [cited, setCited] = useState(null);
  useEffect(() => {
    const ids = members.flatMap((m) => [...(m.messages || []), ...(verdicts?.[m.id]?.evidence || []).map((e) => e.messageId)]).filter(Boolean);
    if (!ids.length || !job.session) { setCited(null); return undefined; }
    const controller = new AbortController();
    fetchSessionTurns({ session: job.session, messageIds: ids, signal: controller.signal }).then(
      (read) => { if (!controller.signal.aborted) setCited(read.turns); },
      () => { if (!controller.signal.aborted) setCited(null); },
    );
    return () => controller.abort();
  }, [members, verdicts, job.session]);
  const placed = useMemo(() => {
    if (!cited) return verdicts;
    const out = { ...verdicts };
    for (const m of members) {
      const v = verdicts?.[m.id];
      if (!v?.evidence?.length) continue;
      out[m.id] = { ...v, evidence: v.evidence.map((e) => (e.turn != null || !e.messageId ? e : { ...e, turn: cited.get(e.messageId) ?? null })) };
    }
    return out;
  }, [members, verdicts, cited]);
  const ribbon = useMemo(() => ribbonBins(members, placed, {
    cited: cited ? (m) => (m.messages || []).map((id) => cited.get(String(id).toLowerCase())) : null,
  }), [members, placed, cited]);
  // Nothing opens by itself: the card and the list are the overview, and a
  // finding already spread under them is more than a reader asked for.
  const [open, setOpen] = useState(null);
  const [hover, setHover] = useState(null);
  const flagged = useMemo(() => members.map((m) => ({
    title: m.title,
    messageIds: [...(m.messages || []), ...((verdicts?.[m.id]?.evidence || []).map((e) => e.messageId).filter(Boolean))],
    met: !!verdicts?.[m.id]?.escalates,
    quotes: (verdicts?.[m.id]?.evidence || []).filter((e) => e.messageId && e.text).map((e) => ({ messageId: e.messageId, text: e.text })),
    standard: verdicts?.[m.id]?.metBy?.[0] || null,
    why: verdicts?.[m.id]?.standardWhy || null,
  })), [members, verdicts]);
  const mixed = job.conducts > 1;
  const choose = (i) => setOpen((cur) => (cur === i ? null : i));
  // Drawn on the card, a finding picked says first which standard it met and
  // why; the list under the card stays off.
  // Null until the card knows: the list waits rather than show and vanish.
  const [storyShown, setStoryShown] = useState(null);
  // The findings in the story that met a standard are told together in a
  // few sentences, from the story the card draws: one summary, however many
  // met. The standards' own wording, where the tab has it, goes with the
  // request, and their sentences stand in where none can be written.
  const suggest = useSyncExternalStore(subscribeSuggestContext, getSuggestContext, () => null);
  const metIn = useCallback((story) => {
    const shown = new Set((story?.flags || []).filter((f) => f.kind === "finding").map((f) => f.finding));
    return members.map((m, i) => ({ m, i, v: verdicts?.[m.id] })).filter(({ i, v }) => v?.escalates && shown.has(i));
  }, [members, verdicts]);
  const toldSummary = useCallback((story) => {
    const met = metIn(story);
    if (!met.length) return null;
    const told = new Set(met.map(({ i }) => i));
    const input = summaryInput({
      findings: met.map(({ m, i, v }) => {
        const label = v.metBy?.[0] || null;
        const item = label ? standardFromLabel(suggest, label) : null;
        return { finding: m, verdict: v, at: i, standard: label ? { name: label, text: item?.text || null } : null };
      }),
      story,
      others: members.filter((_, j) => !told.has(j)).map((o) => o.title).filter(Boolean),
    });
    const whys = met.filter(({ v }) => v.standardWhy).map(({ m, v }) => (
      <p key={m.id} className="m-0 text-[12px] leading-[1.55] text-[color:var(--ink)] [overflow-wrap:anywhere]">
        {met.length > 1 ? <span className="text-[color:var(--muted)]">{m.title}: </span> : null}
        {v.standardWhy}
      </p>
    ));
    const fallback = whys.length > 1 ? <div className="grid gap-[4px]">{whys}</div> : whys[0] || null;
    return (
      <div className="grid gap-[4px]">
        <FindingSummary summaryKey={met.map(({ m, v }) => summaryKey(m.id, v)).join("+")} input={input} fallback={fallback} />
        <SummaryTools met={met.map(({ m, v }) => ({ member: m, verdict: v, conduct: mixed ? null : conductName(m) }))} />
      </div>
    );
  }, [metIn, members, suggest, mixed, conductName]);
  // The findings the summary tells are said there; the rest, under it.
  const aboutFindings = useCallback((indexes, story = null) => {
    const summarized = new Set(metIn(story).map(({ i }) => i));
    const rest = indexes.filter((i) => !summarized.has(i));
    if (!rest.length) return null;
    return rest.map((i) => {
      const m = members[i];
      const v = placed?.[m?.id];
      if (!m) return null;
      return (
        <div key={m.id} data-lite-story-finding={i} className="grid gap-[2px]">
          {v ? <LiteVerdictBrief member={m} verdict={v} conduct={mixed ? null : conductName(m)} /> : <span className="text-[11px] leading-[1.4] text-[color:var(--muted)]">Not checked yet.</span>}
        </div>
      );
    });
  }, [members, placed, mixed, conductName, metIn]);
  const order = members.map((m, i) => ({ m, i }));
  if (mixed) order.sort((a, b) => String(conductName(a.m)).localeCompare(String(conductName(b.m))) || a.i - b.i);
  let lastConduct = null;

  return (
    <div data-lite-session="" className="grid cursor-auto gap-[6px] pt-[2px] pb-[8px]" style={{ paddingLeft: inset, paddingRight: insetEnd }}>
      {/* Drawn on the card, the story says all this; the line waits with the list. */}
      <div className={storyShown !== false ? "hidden" : "flex flex-wrap items-baseline justify-between gap-x-[12px] gap-y-[2px]"}>
        <span className={FILE_LABEL}>The session{" \u00b7 "}{members.length} flagged finding{members.length === 1 ? "" : "s"}</span>
        <button type="button" data-lite-open-session="" onClick={(e) => { e.stopPropagation(); onOpen(members[0]); }} className={ACTION_CHIP}>
          Open the session
        </button>
      </div>
      <TriageSessionPreview session={job.session} summary={summary} endpointId={members.find((m) => m.endpoint)?.endpoint || null} findings={flagged} highlight={hover ?? open} story told={aboutFindings} toldSummary={toldSummary} onTold={setStoryShown} />

      <div className={storyShown !== false ? "hidden" : "grid min-w-0 border-t border-[color:var(--line-soft)] pt-[2px]"}>
        {order.map(({ m, i }) => {
          const v = placed?.[m.id];
          const on = open === i;
          const conduct = conductName(m);
          const header = mixed && conduct !== lastConduct ? conduct : null;
          lastConduct = conduct;
          return (
            <div key={m.id} className="grid">
              {header ? <span className={`${FILE_LABEL} pt-[8px] pb-[2px]`} style={{ paddingLeft: 14 + 8 }}>{header}</span> : null}
              <div data-lite-member={i} className="grid">
                <span
                  onClick={() => choose(i)}
                  onMouseEnter={() => setHover(i)}
                  onMouseLeave={() => setHover(null)}
                  className="flex min-w-0 cursor-pointer items-center gap-x-[8px] rounded-[2px] py-[3px] hover:bg-[color:var(--control-bg)]"
                >
                  <Disclosure open={on} label={on ? "Close what the check found" : "Show what the check found"} onToggle={() => choose(i)} />
                  <span className="w-[22px] shrink-0 text-right text-[9px] leading-[15px] tabular-nums text-[color:var(--muted-soft)]">{i + 1}</span>
                  <SeverityWord severity={m.severity} />
                  <span className="flex min-w-0 flex-1 items-center gap-x-[8px]">
                    <span className={`min-w-0 truncate text-[11px] leading-[1.35] text-[color:var(--ink)] ${v?.escalates ? "font-medium" : ""}`}>{m.title}</span>
                    {v?.escalates ? <span data-lite-member-met="" className={`${CAPS_CHIP} ${RUNG_CHIP.look_now}`}>Met</span> : null}
                  </span>
                  <span className="flex shrink-0 items-center gap-x-[10px]">
                    <span className="flex w-[124px] shrink-0">
                      {v ? <VerdictChip verdict={v} /> : <span className="text-[9px] leading-[15px] tracking-[0.08em] uppercase text-[color:var(--muted)]">not settled</span>}
                    </span>
                    <span className="w-[96px] shrink-0 text-right font-mono text-[10px] leading-[15px] tabular-nums text-[color:var(--muted)]">{range(ribbon.turns[i])}</span>
                  </span>
                </span>
                {/* Why it met, without opening it: the met findings are why the
                    row sits where it does. Open, the card says it. */}
                {v?.escalates && v.standardWhy && !on ? (
                  <span data-lite-member-why="" className="pb-[5px] text-[11px] leading-[1.4] text-[color:var(--muted)]" style={{ paddingLeft: MEMBER_BODY }}>
                    {v.metBy?.length ? <span data-lite-member-met-by="" className="text-[color:var(--ink)]">Met by {v.metBy.join(", ")}: </span> : null}
                    {v.standardWhy}
                    <WrongFlag findings={[{ id: m.id, title: m.title, verdict: v }]} />
                  </span>
                ) : null}
                {on && v ? <LiteVerdictCard member={m} verdict={v} conduct={mixed ? null : conduct} onOpen={onOpen} /> : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
